import React, { useEffect, useMemo, useState } from "react";
import Layout from "../Layout";
import { apiGetJson, buildQuery, clamp, fmtDate } from "./gemV2Api";

const COLORS = {
  text: "#1f2937",
  muted: "#6b7280",
  border: "#e5e7eb",
  border2: "#f3f4f6",
  bgHead: "#f9fafb",
};

const containerStyle = { maxWidth: 1480, margin: "0 auto", padding: 16, color: COLORS.text };
const cardStyle = { border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 12, background: "#fff" };
const rowStyle = { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" };
const inputStyle = { padding: "10px 12px", borderRadius: 10, border: "1px solid #d1d5db" };
const searchStyle = { ...inputStyle, minWidth: 320 };
const selectStyle = { ...inputStyle };
const btnStyle = { padding: "9px 12px", borderRadius: 10, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" };
const smallMuted = { color: COLORS.muted, fontSize: 12 };
const tableWrap = { overflowX: "auto", borderRadius: 12, border: `1px solid ${COLORS.border}` };
const thStyle = {
  textAlign: "left",
  padding: 10,
  background: COLORS.bgHead,
  borderBottom: `1px solid ${COLORS.border}`,
  verticalAlign: "top",
  whiteSpace: "normal",
  lineHeight: 1.2,
};
const tdStyle = {
  padding: 10,
  borderBottom: `1px solid ${COLORS.border2}`,
  verticalAlign: "top",
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  lineHeight: 1.25,
};
const headerFilterInputStyle = {
  width: "100%",
  minWidth: 0,
  padding: "6px 7px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
};
const headerFilterDateStyle = { ...headerFilterInputStyle, padding: "4px 6px" };

const NUMERIC_MIN_FILTER_KEYS = new Set(["total_value", "ordered_qty", "price", "unit_price"]);

const COLUMNS = [
  { key: "contract_no", label: "Contract No", width: 190, kind: "text", noWrap: true },
  { key: "organization_type", label: "Org Type", width: 120, kind: "text" },
  { key: "ministry_or_state", label: "Min / State", width: 140, kind: "text" },
  { key: "department", label: "Department", width: 140, kind: "text" },
  { key: "organization_name", label: "Organization Name", width: 180, kind: "text" },
  { key: "office_zone", label: "Office Zone", width: 110, kind: "text" },
  { key: "buyer_designation", label: "Buyer Designation", width: 140, kind: "text" },
  { key: "buying_mode", label: "Buying Mode", width: 95, kind: "text" },
  { key: "bid_number", label: "Bid Number", width: 120, kind: "text" },
  { key: "contract_ts", label: "Contract Time", width: 140, kind: "date" },
  { key: "total_value", label: "Total Value", width: 105, kind: "numeric" },
  { key: "product_name", label: "Product Name", width: 220, kind: "text" },
  { key: "product_brand_name", label: "Product Brand", width: 130, kind: "text" },
  { key: "product_model_name", label: "Product Model", width: 130, kind: "text" },
  { key: "service_name", label: "Service Name", width: 220, kind: "text" },
  { key: "service_category_name", label: "Service Category", width: 150, kind: "text" },
  { key: "ordered_qty", label: "Ordered Qty", width: 95, kind: "numeric" },
  { key: "price", label: "Price", width: 95, kind: "numeric" },
  { key: "unit_price", label: "Unit Price", width: 95, kind: "numeric" },
];

const STRING_FILTER_KEYS = COLUMNS.filter((c) => c.kind === "text").map((c) => c.key);
const SORTABLE_FIELDS = new Set(COLUMNS.map((c) => c.key));

function useDebouncedValue(value, delayMs = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function text(v) {
  return v == null ? "" : String(v);
}

function moneyLike(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function SortHeader({ col, sortBy, sortDir, onSort, loading }) {
  const active = sortBy === col.key;
  const sortable = SORTABLE_FIELDS.has(col.key);
  const arrow = sortable ? (active ? (sortDir === "ASC" ? " \u25B2" : " \u25BC") : " \u2195") : "";
  return (
    <th
      style={{
        ...thStyle,
        width: col.width,
        minWidth: col.width,
        maxWidth: col.width,
        whiteSpace: col.noWrap ? "nowrap" : thStyle.whiteSpace,
        cursor: sortable && !loading ? "pointer" : "default",
        opacity: loading ? 0.7 : 1,
        fontWeight: active ? 800 : 700,
      }}
      onClick={() => !loading && sortable && onSort(col.key)}
      title={sortable ? "Click to sort" : ""}
    >
      {col.label}
      {sortable ? <span style={{ color: active ? COLORS.text : "#9ca3af" }}>{arrow}</span> : null}
    </th>
  );
}

function Pagination({ page, totalPages, onPage, loading }) {
  const safeTotal = Math.max(1, totalPages || 1);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <button style={btnStyle} onClick={() => onPage(clamp(page - 1, 1, safeTotal))} disabled={page <= 1 || loading}>
        Prev
      </button>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={smallMuted}>Page</span>
        <input
          style={{ ...inputStyle, width: 84, minWidth: 84 }}
          value={page}
          onChange={(e) => {
            const v = parseInt(e.target.value || "1", 10);
            onPage(clamp(Number.isNaN(v) ? 1 : v, 1, safeTotal));
          }}
        />
        <span style={smallMuted}>of {safeTotal}</span>
        {loading ? <span className="tm-spinner" title="Loading" /> : null}
      </div>
      <button style={btnStyle} onClick={() => onPage(clamp(page + 1, 1, safeTotal))} disabled={page >= safeTotal || loading}>
        Next
      </button>
    </div>
  );
}

function HighlightText({ value, search, filterTerm }) {
  const raw = text(value);
  const tokens = [
    ...String(search || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    ...String(filterTerm || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
  ];
  const dedupedTokens = tokens.filter((t, i) => tokens.findIndex((x) => x.toLowerCase() === t.toLowerCase()) === i);
  if (!raw || !dedupedTokens.length) return <>{raw || "-"}</>;
  const escaped = dedupedTokens.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "ig");
  const parts = raw.split(re);
  return (
    <>
      {parts.map((part, i) => {
        const hit = dedupedTokens.some((t) => t.toLowerCase() === part.toLowerCase());
        return hit ? (
          <mark key={i} style={{ backgroundColor: "#fef08a", padding: "0 1px" }}>
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        );
      })}
    </>
  );
}

export default function GeMContractsDashboardPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("contract_ts");
  const [sortDir, setSortDir] = useState("DESC");

  const [filters, setFilters] = useState({
    contract_no: "",
    organization_type: "",
    ministry_or_state: "",
    department: "",
    organization_name: "",
    office_zone: "",
    buyer_designation: "",
    buying_mode: "",
    bid_number: "",
    product_name: "",
    product_brand_name: "",
    product_model_name: "",
    service_name: "",
    service_category_name: "",
    dateFrom: "",
    dateTo: "",
    minTotal: "",
    minOrderedQty: "",
    minPrice: "",
    minUnitPrice: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resp, setResp] = useState({ ok: true, totalRows: 0, uniqueContractNos: 0, rows: [] });

  const search = useDebouncedValue(searchInput.trim(), 350);
  const debouncedFilters = useDebouncedValue(filters, 350);

  useEffect(() => {
    setPage(1);
  }, [search, debouncedFilters, pageSize, sortBy, sortDir]);

  const query = useMemo(() => {
    const q = {
      page,
      pageSize,
      q: search,
      sortBy,
      sortDir: sortDir.toLowerCase(),
      dateFrom: debouncedFilters.dateFrom,
      dateTo: debouncedFilters.dateTo,
      minTotal: debouncedFilters.minTotal,
      minOrderedQty: debouncedFilters.minOrderedQty,
      minPrice: debouncedFilters.minPrice,
      minUnitPrice: debouncedFilters.minUnitPrice,
    };
    STRING_FILTER_KEYS.forEach((k) => {
      q[`f_${k}`] = debouncedFilters[k];
    });
    return buildQuery(q);
  }, [page, pageSize, search, sortBy, sortDir, debouncedFilters]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const json = await apiGetJson(`/api/gem-contracts-dashboard${query}`, "results");
        if (!mounted) return;
        setResp(json || { ok: true, totalRows: 0, uniqueContractNos: 0, rows: [] });
      } catch (e) {
        if (!mounted) return;
        setError(e.message || String(e));
        setResp({ ok: false, totalRows: 0, uniqueContractNos: 0, rows: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [query]);

  function onSort(field) {
    if (!SORTABLE_FIELDS.has(field)) return;
    if (sortBy === field) setSortDir((s) => (s === "ASC" ? "DESC" : "ASC"));
    else {
      setSortBy(field);
      setSortDir("DESC");
    }
  }

  function setFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function clearAllFilters() {
    setSearchInput("");
    setFilters({
      contract_no: "",
      organization_type: "",
      ministry_or_state: "",
      department: "",
      organization_name: "",
      office_zone: "",
      buyer_designation: "",
      buying_mode: "",
      bid_number: "",
      product_name: "",
      product_brand_name: "",
      product_model_name: "",
      service_name: "",
      service_category_name: "",
      dateFrom: "",
      dateTo: "",
      minTotal: "",
      minOrderedQty: "",
      minPrice: "",
      minUnitPrice: "",
    });
    setPage(1);
  }

  const rows = Array.isArray(resp?.rows) ? resp.rows : [];
  const totalRows = Number(resp?.totalRows || 0);
  const uniqueContractNos = Number(resp?.uniqueContractNos || 0);
  const totalPages = Math.max(1, Math.ceil(totalRows / Math.max(1, pageSize)));

  return (
    <Layout>
      <div style={containerStyle}>
        <style>{`
          .tm-loadingbar-wrap{
            height: 3px; border-radius: 999px; background: #eef2ff;
            overflow: hidden; margin-top: 10px;
          }
          .tm-loadingbar{
            height: 100%;
            width: 40%;
            background: #111827;
            transform: translateX(-120%);
            animation: tmbar 1.1s infinite;
          }
          @keyframes tmbar{
            0%{ transform: translateX(-120%); opacity: .35; }
            50%{ opacity: .9; }
            100%{ transform: translateX(260%); opacity: .35; }
          }
          .tm-spinner{
            width: 14px; height: 14px; border-radius: 999px;
            border: 2px solid #e5e7eb; border-top-color: #111827;
            animation: tmspin .8s linear infinite;
            display: inline-block;
          }
          @keyframes tmspin{
            from{ transform: rotate(0deg); }
            to{ transform: rotate(360deg); }
          }
        `}</style>

        <div style={{ ...cardStyle, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, display: "flex", gap: 10, alignItems: "center" }}>
                <span>GeM Contracts Dashboard</span>
                {loading ? <span className="tm-spinner" title="Loading" /> : null}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontWeight: 800 }}>{uniqueContractNos.toLocaleString()} unique contracts</div>
              <div style={smallMuted}>{totalRows.toLocaleString()} rows</div>
            </div>
          </div>

          {loading ? (
            <div className="tm-loadingbar-wrap" title="Loading">
              <div className="tm-loadingbar" />
            </div>
          ) : null}

          <div style={{ marginTop: 12, ...rowStyle }}>
            <input
              style={searchStyle}
              placeholder="Search org, product, ministry...(comma-separated)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />

<button style={btnStyle} onClick={clearAllFilters}>
              Clear Filters
            </button>

            <div style={{ marginLeft: "auto" }}>
              <Pagination page={page} totalPages={totalPages} onPage={setPage} loading={loading} />
            </div>
          </div>

          {error ? <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: 700 }}>Error: {error}</div> : null}
        </div>

        <div style={{ ...cardStyle, marginBottom: 12 }}>
          <div style={smallMuted}>
            Showing {rows.length ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, totalRows)} of {totalRows}
          </div>
        </div>

        <div style={tableWrap}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <SortHeader key={col.key} col={col} sortBy={sortBy} sortDir={sortDir} onSort={onSort} loading={loading} />
                ))}
              </tr>
              <tr>
                {COLUMNS.map((col) => {
                  const baseTh = {
                    ...thStyle,
                    background: "#fff",
                    padding: 6,
                    width: col.width,
                    minWidth: col.width,
                    maxWidth: col.width,
                  };

                  if (col.key === "contract_ts") {
                    return (
                      <th key={`${col.key}_filter`} style={baseTh}>
                        <div style={{ display: "grid", gap: 4 }}>
                          <input
                            type="date"
                            style={headerFilterDateStyle}
                            value={filters.dateFrom}
                            onChange={(e) => setFilter("dateFrom", e.target.value)}
                            title="From date"
                          />
                          <input
                            type="date"
                            style={headerFilterDateStyle}
                            value={filters.dateTo}
                            onChange={(e) => setFilter("dateTo", e.target.value)}
                            title="To date"
                          />
                        </div>
                      </th>
                    );
                  }

                  if (NUMERIC_MIN_FILTER_KEYS.has(col.key)) {
                    const mapKey =
                      col.key === "total_value"
                        ? "minTotal"
                        : col.key === "ordered_qty"
                          ? "minOrderedQty"
                          : col.key === "price"
                            ? "minPrice"
                            : "minUnitPrice";
                    return (
                      <th key={`${col.key}_filter`} style={baseTh}>
                        <input
                          type="number"
                          step="0.01"
                          style={headerFilterInputStyle}
                          placeholder="Min"
                          value={filters[mapKey]}
                          onChange={(e) => setFilter(mapKey, e.target.value)}
                        />
                      </th>
                    );
                  }

                  return (
                    <th key={`${col.key}_filter`} style={baseTh}>
                      <input
                        style={headerFilterInputStyle}
                        placeholder="Filter"
                        value={filters[col.key] || ""}
                        onChange={(e) => setFilter(col.key, e.target.value)}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ ...tdStyle, color: COLORS.muted }}>
                    No rows found for current filters.
                  </td>
                </tr>
              ) : null}

              {rows.map((r, idx) => (
                <tr key={`${text(r.contract_no)}|${text(r.bid_number)}|${idx}`}>
                  {COLUMNS.map((col) => {
                    let value = r?.[col.key];
                    if (col.kind === "date") value = fmtDate(value);
                    if (col.kind === "numeric") value = moneyLike(value);
                    const isNumeric = col.kind === "numeric";
                    return (
                      <td
                        key={col.key}
                        style={{
                          ...tdStyle,
                          width: col.width,
                          minWidth: col.width,
                          maxWidth: col.width,
                          whiteSpace: col.noWrap ? "nowrap" : tdStyle.whiteSpace,
                          wordBreak: col.noWrap ? "normal" : tdStyle.wordBreak,
                          overflowWrap: col.noWrap ? "normal" : tdStyle.overflowWrap,
                          textAlign: isNumeric ? "right" : "left",
                        }}
                        title={text(value)}
                      >
                        {isNumeric ? (
                          text(value) || "-"
                        ) : (
                          <HighlightText
                            value={value}
                            search={search}
                            filterTerm={col.kind === "text" ? debouncedFilters[col.key] : ""}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ ...cardStyle, marginTop: 12 }}>
          <Pagination page={page} totalPages={totalPages} onPage={setPage} loading={loading} />
        </div>
      </div>
    </Layout>
  );
}
